/* eslint-disable react/jsx-props-no-spreading */
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"
import { useTheme } from "@mui/material"
import ButtonGroup from "@mui/material/ButtonGroup"
import ClickAwayListener from "@mui/material/ClickAwayListener"
import Grow from "@mui/material/Grow"
import MenuItem from "@mui/material/MenuItem"
import MenuList from "@mui/material/MenuList"
import Popper from "@mui/material/Popper"
import * as React from "react"

import AppTooltip from "../../helper/appTooltip"
import AppPaper from "../../surfaces/appPaper"
import AppButton from "../appButton"

interface IAction {
    title: string
    onClick: () => void
    disabled?: boolean
    tooltip?: string
}

interface ISplitButtonProps {
    actions: IAction[]
}

/**
 * A button that contains a list of actions to choose from
 * @example
 * <SplitButton
 *     actions={[
 *         {
 *             title: "Action 1",
 *             onClick: () => console.log("Action 1 clicked"),
 *         },
 *         {
 *             title: "Action 2",
 *             onClick: () => console.log("Action 2 clicked"),
 *         },
 *     ]}
 * />
 */
const SplitButton: React.FC<ISplitButtonProps> = ({ actions }) => {
    const theme = useTheme()
    const [open, setOpen] = React.useState(false)
    const anchorRef = React.useRef<HTMLDivElement>(null)
    const [selectedIndex, setSelectedIndex] = React.useState(0)

    const handleMenuItemClick = (
        event: React.MouseEvent<HTMLLIElement, MouseEvent>,
        index: number
    ): void => {
        setSelectedIndex(index)
        setOpen(false)
    }

    const handleToggle = (): void => {
        setOpen((prevOpen) => !prevOpen)
    }

    const handleClose = (event: Event): void => {
        if (
            anchorRef.current &&
            anchorRef.current.contains(event.target as HTMLElement)
        ) {
            return
        }

        setOpen(false)
    }

    return (
        <>
            <ButtonGroup
                ref={anchorRef}
                aria-label="Button group with a nested menu"
                variant="contained"
                sx={{ boxShadow: "none" }}
                size="large"
            >
                <AppTooltip title={actions[selectedIndex].tooltip ?? ""}>
                    <span>
                        <AppButton
                            onClick={actions[selectedIndex].onClick}
                            disabled={actions[selectedIndex].disabled}
                        >
                            {actions[selectedIndex].title}
                        </AppButton>
                    </span>
                </AppTooltip>
                <AppButton
                    aria-controls={open ? "split-button-menu" : undefined}
                    aria-expanded={open ? "true" : undefined}
                    aria-label="select merge strategy"
                    aria-haspopup="menu"
                    onClick={handleToggle}
                >
                    <ArrowDropDownIcon />
                </AppButton>
            </ButtonGroup>
            <Popper
                sx={{
                    zIndex: 1,
                }}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin:
                                placement === "bottom"
                                    ? "center top"
                                    : "center bottom",
                        }}
                    >
                        <AppPaper
                            sx={{
                                p: 0,
                                backgroundColor:
                                    theme.palette.background.contentBackground,
                            }}
                        >
                            <ClickAwayListener onClickAway={handleClose}>
                                <MenuList id="split-button-menu" autoFocusItem>
                                    {actions.map((action, index) => (
                                        <MenuItem
                                            key={action.title}
                                            selected={index === selectedIndex}
                                            onClick={(event) =>
                                                handleMenuItemClick(
                                                    event,
                                                    index
                                                )
                                            }
                                        >
                                            {action.title}
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </ClickAwayListener>
                        </AppPaper>
                    </Grow>
                )}
            </Popper>
        </>
    )
}

export default SplitButton
